import { GroupCard } from '../../component/groups/group-card'
import { GroupCardSkeleton } from '../../component/groups/group-card-skeleton'
import { PageContainer } from 'src/component/page-container/page-container'
import { Grid } from '../../component/ui/grid/grid'
import { PageTitle } from '../../component/ui/page-title'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDiscoverGroups, useJoinPublicGroup, useMyGroups } from 'src/hook/use-groups'
import { isFeatureFlagged } from 'src/util/feature-flags'
import { toasterUtil } from 'src/util/toaster-util'

type Tab = 'my-groups' | 'discover'

export const GroupsScreen = (): React.ReactElement => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('my-groups')
  const [searchQuery, setSearchQuery] = useState('')
  const [joiningGroupId, setJoiningGroupId] = useState<string | null>(null)

  useEffect(() => {
    if (isFeatureFlagged('GROUPS')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const { data: myGroups = [], isLoading: isLoadingMyGroups, isError: isErrorMyGroups } = useMyGroups()
  const {
    data: discoverData,
    isLoading: isLoadingDiscover,
    isError: isErrorDiscover,
  } = useDiscoverGroups({
    limit: 50,
    offset: 0,
    search: searchQuery,
  })
  const joinGroupMutation = useJoinPublicGroup()

  const discoverGroups = discoverData?.data ?? []

  if (isErrorMyGroups) {
    toasterUtil.error('Failed to load your groups. Please try again.')
  }

  if (isErrorDiscover) {
    toasterUtil.error('Failed to load discover groups. Please try again.')
  }

  const handleJoinGroup = async (groupId: string): Promise<void> => {
    try {
      setJoiningGroupId(groupId)
      await joinGroupMutation.mutateAsync(groupId)
      toasterUtil.success('Successfully joined group!')
    } catch (error: any) {
      toasterUtil.error(error?.message || 'Failed to join group. Please try again.')
    } finally {
      setJoiningGroupId(null)
    }
  }

  const sortedDiscoverGroups = [...discoverGroups].sort((a, b) => {
    if (a.name === 'GRNDHOUSE Community') return -1
    if (b.name === 'GRNDHOUSE Community') return 1
    return (b.memberCount ?? 0) - (a.memberCount ?? 0)
  })

  const isLoading = activeTab === 'my-groups' ? isLoadingMyGroups : isLoadingDiscover
  const groups = activeTab === 'my-groups' ? myGroups : sortedDiscoverGroups
  const hasGroups = groups.length > 0
  const noGroups = !isLoading && !hasGroups

  return (
    <PageContainer>
      <div className="py-6 md:py-[82px]">
        <div className="mb-10">
          <PageTitle>Groups</PageTitle>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8 border-b border-gray-700">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('my-groups')}
              className={`pb-4 font-semibold transition-colors relative ${
                activeTab === 'my-groups' ? 'text-[#C76633]' : 'text-gray-400 hover:text-ivory'
              }`}
            >
              My Groups
              {activeTab === 'my-groups' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C76633]" />}
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              className={`pb-4 font-semibold transition-colors relative ${
                activeTab === 'discover' ? 'text-[#C76633]' : 'text-gray-400 hover:text-ivory'
              }`}
            >
              Discover
              {activeTab === 'discover' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#C76633]" />}
            </button>
          </div>
        </div>

        {/* Search Bar (only for Discover tab) */}
        {activeTab === 'discover' && (
          <div className="mb-8">
            <input
              type="text"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 bg-gray-900 text-white placeholder-gray-400 rounded-lg border border-gray-700 focus:border-gold focus:outline-none"
            />
          </div>
        )}

        {/* Groups Grid */}
        {isLoading ? (
          <Grid.Skeleton count={6}>
            <GroupCardSkeleton />
          </Grid.Skeleton>
        ) : noGroups ? (
          <div className="text-center py-20">
            <h3 className="text-ivory text-2xl font-evogria mb-4">
              {activeTab === 'my-groups' ? 'No Groups Yet' : 'No Groups Found'}
            </h3>
            <p className="text-gray-400 mb-6">
              {activeTab === 'my-groups'
                ? "You haven't joined any groups yet. Check out the Discover tab to find groups!"
                : searchQuery
                ? 'Try adjusting your search.'
                : 'No public groups are available yet.'}
            </p>
          </div>
        ) : (
          <Grid
            items={groups}
            keyExtractor={(g) => g.id}
            renderItem={(group) => (
              <GroupCard
                key={group.id}
                group={group}
                onClick={(id) => navigate(`/groups/${id}`)}
                onJoin={activeTab === 'discover' ? handleJoinGroup : undefined}
                isJoined={activeTab === 'discover' ? (group as any).isJoined : undefined}
                isLoading={joiningGroupId === group.id}
                showOfficialBadge={group.name === 'GRNDHOUSE Community'}
              />
            )}
          />
        )}
      </div>
    </PageContainer>
  )
}
